/**
 * AppStoreBadges — "Get it on Google Play" + "Coming soon on App Store" badges.
 *
 * Renders two pill-buttons matching Treasurescare's teal brand.
 * The Google Play link is future-proofed — will 404 until Google approves the
 * production release, then goes live automatically once approved.
 */
import React from "react";
import { Apple } from "lucide-react";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.treasurescare.app";

function GooglePlayIcon({ className = "w-5 h-5" }) {
  // Official Google Play triangular icon in SVG.
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3.6 1.9c-.4.4-.6.9-.6 1.6v17c0 .7.2 1.2.6 1.6l.1.1L13.3 12v-.2L3.7 1.8l-.1.1z" opacity=".85" />
      <path d="M16.5 15.2 13.3 12v-.2l3.2-3.2.1.1 3.8 2.2c1.1.6 1.1 1.7 0 2.3l-3.8 2.1-.1-.1z" opacity=".95" />
      <path d="m16.6 15.1-3.3-3.3-9.7 9.7c.4.4 1 .4 1.7 0l11.3-6.4" />
      <path d="M4.6 2.1c-.7-.4-1.3-.4-1.7 0l9.7 9.7 3.3-3.3-11.3-6.4z" />
    </svg>
  );
}

export function AppStoreBadges({ variant = "default", className = "" }) {
  const isCompact = variant === "compact";
  const containerCls = isCompact
    ? "inline-flex flex-wrap items-center gap-2"
    : "flex flex-wrap items-center gap-3";
  const btnBase =
    "inline-flex items-center gap-2.5 rounded-xl border transition-all";
  const btnSize = isCompact ? "px-3 py-2" : "px-4 py-2.5";

  return (
    <div className={`${containerCls} ${className}`} data-testid="app-store-badges">
      {/* Google Play — LIVE */}
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`${btnBase} ${btnSize} bg-primary text-primary-foreground border-primary hover:opacity-90 shadow-sm hover:-translate-y-0.5 active:translate-y-0`}
        data-testid="badge-google-play"
        aria-label="Get Treasurescare on Google Play"
      >
        <GooglePlayIcon className="w-6 h-6 text-secondary shrink-0" />
        <div className="text-left leading-tight">
          <div className="text-[9px] uppercase tracking-widest opacity-80">Get it on</div>
          <div className="text-sm font-semibold -mt-0.5">Google Play</div>
        </div>
      </a>

      {/* App Store — COMING SOON */}
      <div
        className={`${btnBase} ${btnSize} bg-muted/50 text-muted-foreground border-border cursor-not-allowed relative`}
        data-testid="badge-app-store"
        aria-label="Treasurescare coming soon on the App Store"
        title="iOS launch coming soon"
      >
        <Apple className="w-6 h-6 shrink-0" />
        <div className="text-left leading-tight">
          <div className="text-[9px] uppercase tracking-widest opacity-80">Coming soon to</div>
          <div className="text-sm font-semibold -mt-0.5">App Store</div>
        </div>
        <span className="absolute -top-1.5 -right-1.5 bg-secondary text-teal text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider">
          Soon
        </span>
      </div>
    </div>
  );
}

export default AppStoreBadges;
